import { useEffect, useRef } from 'react'
import { ApolloError } from '@apollo/client'
import EventEmitter from '../../classes/EventEmiter'
import useRunWorkflowStepMutation from '../../graphQL/mutations/runWorkflowStep'
import { useLazyWorkflowStepRequest } from '../../graphQL/queries/workflowStepRequest'
import {
  IWorkflowStepRequestState,
  WorkflowStepStatus,
} from '../../graphQL/queries/workflowStepRequest/types'
import type { ICinnamonAction } from '../../interfaces/actions'
import { LoadersType } from '../../interfaces'
import { addLoader, removeLoader } from '../../redux/slices/config'
import {
  ORDER_POLL_FIRST_LOOK_MS,
  ORDER_POLL_MAX_TIMEOUT_MS,
  ORDER_POLL_MIN_INTERVAL_MS,
} from '../../redux/slices/orders/types'
import { positiveNumber } from '../../utils/orderConfirmation'
import { runCinnamonActions } from '../../utils/processActions'
import { reportOrdersBackgroundError } from '../orders/ordersHookHelpers'
import usePollChain from '../polling/usePollChain'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'

const FUNCTION_NAME = 'useWorkflowStep'

/** `wait.pollingTime` when none is authored. */
export const WORKFLOW_STEP_POLL_INTERVAL_MS = 2000
/** `wait.timeout` when none is authored. */
export const WORKFLOW_STEP_TIMEOUT_MS = 30000

/**
 * The args of `templateActions.workflow.runStep`, as authored in SCS config.
 *
 * `onOutcome`, `onTimeout` and `onError` arrive unresolved: the config,
 * render and click passes all defer them for this action
 * (`DEFERRED_ACTION_ARGS`), and they are resolved when they run, with
 * `this` = `{ ...data, outcome }` from the step's callback and the store as
 * it is then (`handlerSources`). `outcome` is
 * spread last, so it always names the branch that ran: a step whose `data`
 * carries an `outcome` key of its own cannot shadow it. A run given
 * `internals.orderUid` also puts `order: { id }` in `this`, for every
 * handler (see `runHandlers`).
 */
export interface IWorkflowRunStepArgs {
  /** The step key, as listed in the storefront's `webviewSteps`. */
  step: string
  /** Passed to the step as is. At most 4096 bytes as JSON. */
  params?: Record<string, unknown>
  /**
   * A plain object → wait for the outcome. Anything else (absent, `null`, an
   * array, `true`, `"false"`, `0`) → fire and forget: see `waitOf`. `{}`
   * waits with the defaults.
   *
   * `pollingTime` defaults to 2000 and is floored at
   * `ORDER_POLL_MIN_INTERVAL_MS`; `timeout` defaults to 30000 and is capped
   * at `ORDER_POLL_MAX_TIMEOUT_MS`. Both are read through `positiveNumber`.
   */
  wait?: { pollingTime?: number; timeout?: number }
  /**
   * Hold `LoadersType.WORKFLOW_STEP` up for the wait. Only with `wait`, and
   * only for literal `true`.
   */
  loader?: boolean
  /** Actions per outcome name. A name with no entry runs `onError`. */
  onOutcome?: Record<string, ICinnamonAction[]>
  /** The request expired, or the wait ran out. */
  onTimeout?: ICinnamonAction[]
  /** The trigger was refused, a request failed, or the outcome was unmapped. */
  onError?: ICinnamonAction[]
}

/**
 * Values a caller in code may add, which config cannot author.
 *
 * `orderUid` is the one COM-1296 needs: the storefront reads the order from
 * its own database and hands it to the step, so letting config name it would
 * let an author point a step at an arbitrary order id. The handlers see it as
 * `{{this.order.id}}`.
 */
export interface IWorkflowRunStepInternals {
  orderUid?: string
  /**
   * Awaited once the run has a verdict that runs a handler, before the
   * handler runs, with the step's loader still up (COM-1296). Never for
   * `sent` or `stopped`. The after-order steps pass one that resolves once
   * the screen after the cart has mounted: a step can answer before the
   * cart's push to `pushAfter` has landed (a trigger that fails at once,
   * or a fast answer on a slow device), and its handlers would then reach
   * the cart. A rejection is reported and the handler runs anyway.
   */
  beforeHandlers?: () => Promise<void>
  /**
   * Sources beside `this` for the handlers' single resolution pass, read
   * when each handler list runs (COM-1296 review). A getter, so the store is
   * read when the handler runs, not when the step started. `this` is still
   * the step's answer whatever it returns. Defaults to
   * `() => ({ state: store.getState() })`, which is what every caller needs:
   * no earlier pass resolves a handler (`DEFERRED_ACTION_ARGS`, PR #843
   * review), so without the store a `{{params.*}}`, `{{texts.*}}` or
   * `{{globals.*}}` in a handler resolves to nothing. The after-order steps
   * pass the same getter explicitly.
   */
  handlerSources?: () => Record<string, unknown>
}

/**
 * How a run ended. Returned from `runStep` so a caller in code can await it —
 * which is what COM-1296's place-order queue and after-order steps need.
 *
 * - `sent` — fire-and-forget, the trigger was accepted.
 * - `outcome` — `COMPLETED` with a mapped outcome; its actions ran.
 * - `unmapped` — `COMPLETED` with an outcome `onOutcome` has no entry for.
 * - `timeout` — `EXPIRED`, the wait's deadline passed, or (with `wait`) the
 *   trigger itself did not answer within `timeout`.
 * - `error` — `reason` says which: `trigger_failed` (the trigger was refused,
 *   or a poll read `TRIGGER_FAILED`), `no_request_id`, `poll_failed` (a poll
 *   answered with a GraphQL error), `unknown_status` (a poll read a status
 *   this build does not know), or `run_failed` (the run itself threw before
 *   it reached a verdict, such as a `dispatch` throwing; reported to RUM, and
 *   `onError` runs as for any other error).
 * - `stopped` — no handler runs. The run was superseded by a newer waiting
 *   run, or its owner unmounted (with `stopOnUnmount`) at any point after the
 *   run began, including while the trigger was in flight (a waiting run
 *   resolves at the unmount itself; see `runStep`). A call that joined such
 *   a run gets the same `stopped`.
 *
 * A handler that throws (a `null` entry in an authored list, an action named
 * `constructor`) does not change the result: the throw is reported to RUM
 * and the run still resolves with the kind its verdict named.
 *
 * `handled` (on `timeout`, `error` and `unmapped`) says whether the run had a
 * handler for that failure and ran it: `onTimeout` for a timeout, `onError`
 * for the other two, and only a non-empty list counts, since `[]` runs
 * nothing (COM-1296). It describes the run that produced the result, so a
 * call that **joined** a run in flight learns about the original caller's
 * handlers, not about its own `args`, which a join ignores. That is what the
 * place-order queue reads to decide whether to show its generic alert. A
 * handler list that throws part-way still counts: it ran, and the throw is
 * reported.
 */
export type WorkflowStepResult =
  | { kind: 'sent' }
  | { kind: 'outcome'; outcome: string }
  | { kind: 'unmapped'; outcome: string; handled: boolean }
  | { kind: 'timeout'; handled: boolean }
  | { kind: 'error'; reason: WorkflowStepErrorReason; handled: boolean }
  | { kind: 'stopped' }

export type WorkflowStepErrorReason =
  | 'trigger_failed'
  | 'no_request_id'
  | 'poll_failed'
  | 'unknown_status'
  | 'run_failed'

/**
 * Which phase ran out of time, for the RUM report only: `trigger` is the
 * mutation not answering, `wait` the poll's deadline or an `EXPIRED` read.
 */
type TimeoutPhase = 'trigger' | 'wait'

/** What a poll ended with, before any handler runs. */
type PollVerdict =
  | { kind: 'state'; state: IWorkflowStepRequestState }
  | { kind: 'timeout'; phase: TimeoutPhase }
  | { kind: 'error'; reason: WorkflowStepErrorReason }
  | { kind: 'stopped' }

/**
 * How the trigger mutation ended. `timed_out` and `stopped` are produced by
 * `boundTrigger`, not by the mutation: the trigger did not answer within
 * `timeout`, or the owner unmounted (with `stopOnUnmount`) before it did.
 */
type TriggerAnswer =
  | { kind: 'answered'; requestId: string | null | undefined }
  | { kind: 'failed' }
  | { kind: 'timed_out' }
  | { kind: 'stopped' }

type WaitArgs = NonNullable<IWorkflowRunStepArgs['wait']>

export interface UseWorkflowStepOptions {
  /**
   * Whether the owner unmounting stops a wait. Defaults to `true`: the
   * handlers emit on the owner's `EventManager`, which unmounting tears down,
   * so there is nothing left for them to run on. A caller whose emitter
   * outlives it (COM-1296's after-order steps) can pass `false`.
   */
  stopOnUnmount?: boolean
}

/**
 * The server answered with an error, as opposed to the request not arriving.
 * Only the second is worth another tick.
 */
const isServerAnswer = (exception: unknown): boolean =>
  exception instanceof ApolloError && exception.graphQLErrors.length > 0

/**
 * Reports to RUM through `reportOrdersBackgroundError`, which does not start
 * a view (a step running behind the screen is not a navigation) and lifts
 * every GraphQL code and message and the network error's message into their
 * own facets (`graphQLErrorCodes`, `graphQLErrorMessages`,
 * `networkErrorMessage`).
 *
 * `params` is never reported: no caller passes it in `extra`. It is authored
 * per tenant and can carry shopper data through `{{cart.*}}` bindings.
 */
const reportWorkflowStepError = (
  exception: unknown,
  title: string,
  extra: Record<string, unknown>,
) => {
  reportOrdersBackgroundError(exception, FUNCTION_NAME, title, extra)
}

/**
 * The authored `wait`, or `undefined` for fire-and-forget.
 *
 * Only a plain object waits. The args are untyped remote JSON, so a
 * truthiness test would read `"wait": "false"` or `"wait": "0"` as a wait
 * and hold a 30 s poll (and the loader) for an author who asked for none.
 */
const waitOf = (value: unknown): WaitArgs | undefined => {
  const isPlainObject =
    typeof value === 'object' && value !== null && !Array.isArray(value)
  if (!isPlainObject) {
    return undefined
  }
  return value as WaitArgs
}

/** The poll's budget, read off an authored `wait`. */
const timeoutOf = (wait: WaitArgs): number =>
  Math.min(
    positiveNumber(wait.timeout, WORKFLOW_STEP_TIMEOUT_MS),
    ORDER_POLL_MAX_TIMEOUT_MS,
  )

/**
 * The trigger's answer; `timed_out` if it has not come by `timeout`; or
 * whatever the function it adds to `settlers` is called with first.
 *
 * Apollo has no request timeout, so without this a hung trigger would hold
 * `LoadersType.WORKFLOW_STEP` up forever. The function added to `settlers`
 * is what the unmount cleanup calls with `stopped`, so a run whose owner
 * unmounts while its trigger is in flight settles at once, and `runStep`'s
 * `finally` takes the loader down then rather than at `timeout`. Whichever
 * comes first clears the timer and removes the function from `settlers`, so
 * a run settled early leaves no timer behind. The request itself is not
 * cancelled.
 *
 * A late answer is dropped: a late success's requestId is not polled,
 * because the run has already resolved (`timeout` or `stopped`), and polling
 * it now would run a second handler for the same run, or one on a dead
 * owner. A late refusal or a late missing requestId is still reported, by
 * `callTrigger`, which produced `answer`, since it describes the server.
 */
const boundTrigger = (
  answer: Promise<TriggerAnswer>,
  timeout: number,
  settlers: Set<(value: TriggerAnswer) => void>,
): Promise<TriggerAnswer> =>
  new Promise<TriggerAnswer>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const settle = (value: TriggerAnswer) => {
      clearTimeout(timer)
      settlers.delete(settle)
      resolve(value)
    }
    timer = setTimeout(() => {
      settle({ kind: 'timed_out' })
    }, timeout)
    settlers.add(settle)
    answer.then(settle)
  })

/** An authored list of actions, or `undefined` for anything else. */
const actionListOf = (value: unknown): ICinnamonAction[] | undefined =>
  Array.isArray(value) ? value : undefined

/**
 * Whether an authored handler list would run anything: a non-empty array.
 * `onError: []` is a list that runs nothing, so it is not a handler.
 */
const hasActions = (value: unknown): boolean => {
  const list = actionListOf(value)
  return list !== undefined && list.length > 0
}

/**
 * The actions mapped to an outcome name.
 *
 * An outcome name is server-sent, and `constructor` and `__proto__` both pass
 * the storefront's pattern, so the index can land on a prototype member. No
 * own-property check is written for that: `actionListOf` accepts arrays only
 * and no member of `Object.prototype` is one, so such a name reads as
 * unmapped. The `constructor` test is the kill case for that reliance.
 *
 * Only a plain object is a map, as in `waitOf`. An array passes `typeof ===
 * 'object'`, so an `onOutcome` authored as a list of lists would let a
 * numeric outcome such as `"0"` index into it and run the inner list; it
 * reads as unmapped instead (PR #843 review).
 */
const outcomeActionsOf = (
  onOutcome: unknown,
  outcome: string,
): ICinnamonAction[] | undefined => {
  if (!onOutcome || typeof onOutcome !== 'object' || Array.isArray(onOutcome)) {
    return undefined
  }
  return actionListOf((onOutcome as Record<string, unknown>)[outcome])
}

/**
 * Runs `templateActions.workflow.runStep` (COM-1295).
 *
 * Without `wait`, calls `runWorkflowStep` once: nothing runs on success, and
 * a failure is reported to RUM and runs `onError`. With `wait`, polls
 * `workflowStepRequest` through `usePollChain` and runs the handler the
 * answer names. A handler that is absent leaves nothing on screen; the case
 * is reported to RUM either way.
 *
 * `eventEmitter` is the owning `EventManager`'s, and is where the handlers
 * are emitted. Outside the order flow only: the place-order queue and
 * after-order steps are COM-1296's, and plug in through `runStep`'s returned
 * promise and its `internals` argument.
 */
export const useWorkflowStep = (
  eventEmitter: EventEmitter,
  { stopOnUnmount = true }: UseWorkflowStepOptions = {},
) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionUid = useAppSelector((state) => state.defaultSlice.sessionId)
  const [runWorkflowStepFn] = useRunWorkflowStepMutation()
  const [getRequestFn] = useLazyWorkflowStepRequest()
  const pollChain = usePollChain<PollVerdict>({
    firstLookMs: ORDER_POLL_FIRST_LOOK_MS,
    stopOnUnmount,
  })
  /**
   * Settles the wait in progress, if any. A run's promise must always settle,
   * or its `finally` never takes the loader down — so a newer run and an
   * unmount both settle it as `stopped`.
   */
  const settleWaitRef = useRef<((verdict: PollVerdict) => void) | null>(null)
  /**
   * Settles each waiting run whose trigger is still in flight; see
   * `boundTrigger`. The unmount cleanup calls them with `stopped`, since such
   * a run has no wait in `settleWaitRef` yet for the cleanup to settle.
   */
  const settleTriggersRef = useRef(new Set<(value: TriggerAnswer) => void>())
  const stopOnUnmountRef = useRef(stopOnUnmount)
  stopOnUnmountRef.current = stopOnUnmount
  /**
   * Set by the unmount cleanup. The cleanup settles a wait in progress and a
   * trigger in flight; this flag is the guard for what can still happen after
   * that, such as a trigger that answered just before the unmount and whose
   * run has not resumed yet, which would otherwise start a wait on the dead
   * instance. Reset on mount, so a `createRoot` StrictMode remount (the test
   * renderer today, the app if it migrates) does not leave the instance
   * marked unmounted.
   */
  const isUnmountedRef = useRef(false)
  /**
   * The run pending on this instance for each step, from `runStep`'s start
   * until its handler is about to run. A second `runStep` for a step in here
   * joins that run and gets its result (COM-1296); see `runStep`.
   */
  const pendingRunsRef = useRef(new Map<string, Promise<WorkflowStepResult>>())
  /**
   * Bumped by every waiting run as it starts. A waiting run holds the value it
   * started with, and one whose trigger answers after a newer waiting run
   * started is superseded, whatever order the triggers answer in.
   */
  const latestWaitRunRef = useRef(0)

  /**
   * The owner has gone and took the run with it. With `stopOnUnmount: false`
   * the run is owed to a caller whose emitter outlives the owner, so it never
   * counts as gone.
   */
  const isStoppedByUnmount = () =>
    isUnmountedRef.current && stopOnUnmountRef.current

  const settleWait = (verdict: PollVerdict) => {
    const settle = settleWaitRef.current
    settleWaitRef.current = null
    settle?.(verdict)
  }

  /** Settles every waiting run whose trigger is still in flight. */
  const settleTriggers = (value: TriggerAnswer) => {
    // A copy: each settler removes itself from the set as it runs.
    const settlers = [...settleTriggersRef.current]
    settlers.forEach((settle) => {
      settle(value)
    })
  }

  useEffect(() => {
    isUnmountedRef.current = false
    return () => {
      isUnmountedRef.current = true
      if (!stopOnUnmountRef.current) {
        return
      }
      settleWait({ kind: 'stopped' })
      settleTriggers({ kind: 'stopped' })
    }
    // Mount and unmount only; `settleWait` and `settleTriggers` read refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * One read of the request. `undefined` keeps the chain polling.
   *
   * `tickFailure` is per wait: a network failure is reported for the first
   * failed tick only, since a device that is offline fails every tick and the
   * deadline's own report says how the wait ended.
   */
  const readRequest = async (
    requestId: string,
    step: string,
    tickFailure: { isReported: boolean },
  ): Promise<PollVerdict | undefined> => {
    try {
      const result = await getRequestFn({
        variables: {
          storefrontName: storefrontName || '',
          sessionUid: sessionUid || '',
          requestId,
        },
      })
      if (result.error) {
        throw result.error
      }
      const state = result.data?.workflowStepRequest
      const isWaiting = !state || state.status === WorkflowStepStatus.WAITING
      if (isWaiting) {
        return undefined
      }
      return { kind: 'state', state }
    } catch (exception) {
      if (isServerAnswer(exception)) {
        reportWorkflowStepError(exception, 'workflow step poll refused', {
          step,
        })
        return { kind: 'error', reason: 'poll_failed' }
      }
      // A request that did not arrive. The next tick may; the deadline is
      // what stops this.
      if (!tickFailure.isReported) {
        tickFailure.isReported = true
        reportWorkflowStepError(exception, 'workflow step poll tick failed', {
          step,
        })
      }
      return undefined
    }
  }

  const waitForOutcome = (
    requestId: string,
    step: string,
    wait: WaitArgs,
  ): Promise<PollVerdict> => {
    const pollingTime = Math.max(
      positiveNumber(wait.pollingTime, WORKFLOW_STEP_POLL_INTERVAL_MS),
      ORDER_POLL_MIN_INTERVAL_MS,
    )
    const timeout = timeoutOf(wait)
    const tickFailure = { isReported: false }

    return new Promise<PollVerdict>((resolve) => {
      settleWait({ kind: 'stopped' })
      settleWaitRef.current = resolve
      pollChain.start({
        tick: () => readRequest(requestId, step, tickFailure),
        onSettle: settleWait,
        onDeadline: () => settleWait({ kind: 'timeout', phase: 'wait' }),
        pollingTime,
        timeout,
      })
    })
  }

  /**
   * Calls the trigger mutation once, and never throws. Reports a refusal and,
   * with `wait` (`isWaiting`), an answer with no requestId.
   *
   * Both reports are made here, on the mutation's own answer, rather than
   * after `boundTrigger`, so they are sent whichever way the run ended first:
   * a trigger that answers after its run timed out, was superseded, or was
   * stopped by an unmount still reports, since the report describes the
   * server, not the screen. Fire-and-forget does not report a missing
   * requestId, because nothing polls with it.
   */
  const callTrigger = async (
    args: IWorkflowRunStepArgs,
    internals: IWorkflowRunStepInternals,
    isWaiting: boolean,
  ): Promise<TriggerAnswer> => {
    const { step, params } = args
    try {
      const result = await runWorkflowStepFn({
        variables: {
          storefrontName: storefrontName || '',
          sessionUid: sessionUid || '',
          step,
          params,
          orderUid: internals.orderUid,
        },
      })
      const requestId = result.data?.runWorkflowStep?.requestId
      if (isWaiting && !requestId) {
        reportWorkflowStepError(
          new Error('workflow step answered no requestId'),
          'workflow step answered no requestId',
          { step },
        )
      }
      return { kind: 'answered', requestId }
    } catch (exception) {
      reportWorkflowStepError(exception, 'workflow step trigger failed', {
        step,
      })
      return { kind: 'failed' }
    }
  }

  /**
   * Triggers the step and, with `wait`, waits for its answer.
   *
   * `waitRun` is the waiting run's token from `latestWaitRunRef`, or `null`
   * for fire-and-forget, which neither takes nor checks one: it starts no
   * wait, so it has nothing to supersede or be superseded in.
   */
  const resolveVerdict = async (
    args: IWorkflowRunStepArgs,
    internals: IWorkflowRunStepInternals,
    wait: WaitArgs | undefined,
    waitRun: number | null,
  ): Promise<PollVerdict | { kind: 'sent' }> => {
    const { step } = args
    const answer = callTrigger(args, internals, wait !== undefined)

    if (!wait) {
      // Fire-and-forget is not bounded: it raises no loader, so a hung
      // trigger holds nothing on screen, and its promise is awaited only by
      // code (COM-1296) that chose not to wait.
      const unbounded = await answer
      if (unbounded.kind === 'failed') {
        return { kind: 'error', reason: 'trigger_failed' }
      }
      return { kind: 'sent' }
    }

    const trigger = await boundTrigger(
      answer,
      timeoutOf(wait),
      settleTriggersRef.current,
    )
    // The owner unmounted while the trigger was in flight, and the cleanup
    // settled this run. A late answer is dropped (see `boundTrigger`).
    if (trigger.kind === 'stopped') {
      return { kind: 'stopped' }
    }
    // A newer waiting run started while this trigger was in flight. Checked
    // here, when the trigger answers, so supersession follows start order
    // and not answer order: an older run answering last must not take the
    // wait over. Reports already made (a refused trigger) still stand.
    const isSuperseded = waitRun !== latestWaitRunRef.current
    if (trigger.kind === 'failed') {
      return isSuperseded
        ? { kind: 'stopped' }
        : { kind: 'error', reason: 'trigger_failed' }
    }
    if (trigger.kind === 'timed_out') {
      return isSuperseded
        ? { kind: 'stopped' }
        : { kind: 'timeout', phase: 'trigger' }
    }
    const { requestId } = trigger
    // Already reported by `callTrigger`. `runStep` resolves the run `stopped`
    // and runs no handler if the owner has gone by now.
    if (!requestId) {
      return isSuperseded
        ? { kind: 'stopped' }
        : { kind: 'error', reason: 'no_request_id' }
    }
    // The owner unmounted after the trigger answered but before this run
    // resumed, so the cleanup found no trigger in flight to settle. The step
    // has run server-side, but nothing is left to wait for it on.
    if (isStoppedByUnmount() || isSuperseded) {
      return { kind: 'stopped' }
    }
    return waitForOutcome(requestId, step, wait)
  }

  /**
   * Runs one authored handler list, and never throws.
   *
   * An authored list can throw from inside `runCinnamonActions`: a `null`
   * entry fails `createFunction`'s destructuring, and an action named
   * `constructor` reaches `Object.prototype.constructor` through
   * `EventEmitter.events`. `runStep` promises its caller a result, and
   * COM-1296 awaits it, so the throw is reported here and the run still
   * resolves with the kind its verdict named. `runCinnamonActions` itself is
   * left throwing: its other callers are component callbacks, where a throw
   * surfaces as it always has.
   */
  const runActions = (
    actions: unknown,
    data: Record<string, unknown>,
    step: string,
    handler: string,
    handlerSources?: () => Record<string, unknown>,
  ) => {
    try {
      // Read inside the `try`, at the moment the list runs.
      const sources = handlerSources ? handlerSources() : {}
      runCinnamonActions(
        actionListOf(actions) ?? [],
        eventEmitter,
        data,
        sources,
      )
    } catch (exception) {
      reportWorkflowStepError(exception, 'workflow step handler threw', {
        step,
        handler,
      })
    }
  }

  /**
   * Runs the handler a verdict names, and says how the run ended, including
   * whether a failure's handler ran (`handled`, see `WorkflowStepResult`).
   *
   * With `internals.orderUid` (an after-order step, COM-1296), every handler
   * sees the order as `{{this.order.id}}`. It is spread after the step's own
   * `data`, so a step cannot replace the id code filled in; `outcome` still
   * goes last.
   */
  const runHandlers = (
    args: IWorkflowRunStepArgs,
    verdict: PollVerdict | { kind: 'sent' },
    internals: IWorkflowRunStepInternals,
  ): WorkflowStepResult => {
    const { step, onOutcome, onTimeout, onError } = args
    // The store as it is when each handler runs: no earlier pass has
    // resolved the handlers (`DEFERRED_ACTION_ARGS`)
    const handlerSources =
      internals.handlerSources ?? (() => ({ state: store.getState() }))
    const context = internals.orderUid
      ? { order: { id: internals.orderUid } }
      : {}
    const runOnError = () => {
      runActions(onError, context, step, 'onError', handlerSources)
    }

    if (verdict.kind === 'timeout') {
      reportWorkflowStepError(
        new Error('workflow step timed out'),
        'workflow step timed out',
        { step, hasHandler: !!actionListOf(onTimeout), phase: verdict.phase },
      )
      runActions(onTimeout, context, step, 'onTimeout', handlerSources)
      return { kind: 'timeout', handled: hasActions(onTimeout) }
    }
    if (verdict.kind === 'error') {
      runOnError()
      return {
        kind: 'error',
        reason: verdict.reason,
        handled: hasActions(onError),
      }
    }
    if (verdict.kind !== 'state') {
      return verdict
    }

    const { status, outcome, data } = verdict.state
    if (status === WorkflowStepStatus.EXPIRED) {
      return runHandlers(args, { kind: 'timeout', phase: 'wait' }, internals)
    }
    if (status === WorkflowStepStatus.TRIGGER_FAILED) {
      // The mutation answers 424 for it first, so a poll reading it means the
      // request was marked after the trigger answered.
      reportWorkflowStepError(
        new Error(`workflow step request ${status}`),
        'workflow step trigger failed',
        { step, status },
      )
      runOnError()
      return {
        kind: 'error',
        reason: 'trigger_failed',
        handled: hasActions(onError),
      }
    }
    if (status !== WorkflowStepStatus.COMPLETED) {
      // A status this build does not know: the storefront added one. Not
      // read as any known branch, since none of them is known to be right.
      reportWorkflowStepError(
        new Error('workflow step unknown status'),
        'workflow step unknown status',
        { step, status },
      )
      runOnError()
      return {
        kind: 'error',
        reason: 'unknown_status',
        handled: hasActions(onError),
      }
    }

    const outcomeName = outcome ?? ''
    // `outcome` last, so a step's own `data.outcome` cannot shadow the name
    // of the branch that ran.
    const outcomeData = { ...(data ?? {}), ...context, outcome: outcomeName }
    const actions = outcomeActionsOf(onOutcome, outcomeName)
    if (!actions) {
      reportWorkflowStepError(
        new Error('workflow step outcome unmapped'),
        'workflow step outcome unmapped',
        { step, outcome: outcomeName },
      )
      runActions(onError, outcomeData, step, 'onError', handlerSources)
      return {
        kind: 'unmapped',
        outcome: outcomeName,
        handled: hasActions(onError),
      }
    }

    runActions(actions, outcomeData, step, 'onOutcome', handlerSources)
    return { kind: 'outcome', outcome: outcomeName }
  }

  /**
   * The `runStep` handler. Resolves once the run has ended and its handler
   * has run; it never rejects — a handler that throws is reported to RUM by
   * `runActions`, and the run's result stands, and a throw before the run has
   * a verdict ends it as `run_failed` (see `startRun`).
   *
   * Two ordering rules, per instance:
   *
   * - **The same step is not run twice at once; a second call joins.**
   *   While a run of `step` is pending (from here until its handler is about
   *   to run), another `runStep` for that `step` returns the same promise as
   *   the run in flight (COM-1296): no mutation, no loader, no report, no
   *   run token, and its own `args` and `internals` are ignored. It resolves
   *   when that run does, with its result, and the handlers run once, in the
   *   original run. A double tap therefore triggers the step once, and a
   *   button and the place-order queue starting the same step share one
   *   verdict. A handler of that run may start the same step again, since
   *   the run stops being pending before its handler runs. Runs on another
   *   instance (the after-order one in `useOrderConfirmation`) are never
   *   joined. Only a `step` that is a non-empty string joins or is joined:
   *   anything else (an unresolved `{{...}}` binding gives `undefined`) names
   *   no step, so it runs on its own and is never recorded as pending, and
   *   two such runs each trigger, fail and report separately (PR #843
   *   review). What the run itself does with that `step` is unchanged.
   * - **Otherwise the newer waiting run wins**, by start order rather than by
   *   the order the triggers answer in. A waiting run whose trigger answers
   *   after a newer waiting run has *started* resolves `stopped`, takes its
   *   own loader down and starts no wait. A newer run that reaches its wait
   *   settles an older wait already in progress as `stopped`. An older wait
   *   still running keeps going until then, so its outcome can still land
   *   while the newer trigger is in flight. Fire-and-forget runs take part in
   *   neither half.
   *
   * With `wait`, the trigger is bounded by the same `timeout` as the wait
   * (see `boundTrigger`) and the wait by its deadline, each on its own timer,
   * so a request that never answers cannot hold the loader up.
   *
   * With `stopOnUnmount`, an owner that unmounted at any point before the
   * handler would run means no handler runs, in either mode: the run
   * resolves `stopped`. `EventManager` has `offAll()`ed the emitter by then,
   * so a handler would be emitted into nothing anyway; returning `stopped`
   * says so rather than reporting a handler as run. RUM reports made before
   * that point (a refused trigger, a missing requestId) are still sent: they
   * describe the server, not the screen. An accepted fire-and-forget trigger
   * still resolves `sent`, since it runs no handler either way; a missing
   * requestId is not reported in that mode, because nothing polls with it.
   *
   * When the run resolves differs by mode. A waiting run settles `stopped` at
   * the unmount itself, even with its trigger still in flight, so its loader
   * comes down then and not when the trigger answers or its `timeout` fires.
   * The request is not cancelled; a late answer polls nothing and runs no
   * handler, and a late refusal or missing requestId is still reported.
   * Fire-and-forget is deliberately not settled early: it holds no loader,
   * so there is nothing to release, and waiting for the answer is what lets
   * it tell a caller in code that the server accepted the step (`sent`). A
   * refusal after the unmount still resolves `stopped`.
   *
   * The loader comes down *before* the handler runs, so an `openDialog` in
   * `onOutcome` is not drawn under the splash, and a nested `runStep` with a
   * loader of its own is not counted against this one. It is raised with the
   * global `loadingDelay` (the splash's debounce) and does not change it.
   */
  const runStep = (
    rawArgs: IWorkflowRunStepArgs,
    internals: IWorkflowRunStepInternals = {},
  ): Promise<WorkflowStepResult> => {
    const args = rawArgs ?? ({} as IWorkflowRunStepArgs)
    const { step } = args
    const pendingRuns = pendingRunsRef.current
    const isJoinable = typeof step === 'string' && step !== ''
    if (!isJoinable) {
      return startRun(args, internals, () => undefined)
    }
    const runInFlight = pendingRuns.get(step)
    if (runInFlight) {
      return runInFlight
    }
    // `startRun` can release before it returns: a throw ahead of its first
    // `await` reaches its `finally` synchronously. Marking that run pending
    // afterwards would leave it in the map for good, and every later run of
    // the step would join its old result.
    let isReleased = false
    const run = startRun(args, internals, () => {
      isReleased = true
      pendingRuns.delete(step)
    })
    if (isReleased) {
      return run
    }
    pendingRuns.set(step, run)
    return run
  }

  /**
   * Awaits `internals.beforeHandlers`, and never throws: a rejection is
   * reported, and the verdict stands, so its handler still runs.
   */
  const holdHandlers = async (
    beforeHandlers: () => Promise<void>,
    step: string,
  ) => {
    try {
      await beforeHandlers()
    } catch (exception) {
      reportWorkflowStepError(exception, 'workflow step handler hold threw', {
        step,
      })
    }
  }

  /**
   * One run of `runStep`, which is not joined to another. `release` ends
   * the run's pending mark, so a later `runStep` for its step starts a run
   * of its own. It is usually called after an `await`, but a throw before the
   * first one releases synchronously; `runStep` handles both.
   *
   * Never rejects. Anything that throws before a verdict (the loader's
   * `dispatch`, the trigger, the wait) is reported to RUM and the run ends as
   * `{ kind: 'error', reason: 'run_failed' }`, whose `onError` runs. The
   * loader is taken down only if it went up: `removeLoader` of a name that is
   * not in the list splices the last entry, someone else's loader.
   */
  const startRun = async (
    args: IWorkflowRunStepArgs,
    internals: IWorkflowRunStepInternals,
    release: () => void,
  ): Promise<WorkflowStepResult> => {
    const wait = waitOf(args.wait)
    const showsLoader = args.loader === true && wait !== undefined
    let waitRun: number | null = null
    if (wait) {
      latestWaitRunRef.current += 1
      waitRun = latestWaitRunRef.current
    }

    let verdict: PollVerdict | { kind: 'sent' }
    let isLoaderUp = false
    try {
      if (showsLoader) {
        // The string form: the object form also writes the global
        // `loadingDelay`, and nothing restores it.
        dispatch(addLoader(LoadersType.WORKFLOW_STEP))
        isLoaderUp = true
      }
      verdict = await resolveVerdict(args, internals, wait, waitRun)
      const runsHandler = verdict.kind !== 'stopped' && verdict.kind !== 'sent'
      if (runsHandler && internals.beforeHandlers) {
        await holdHandlers(internals.beforeHandlers, args.step)
      }
    } catch (exception) {
      reportWorkflowStepError(exception, 'workflow step run threw', {
        step: args.step,
      })
      verdict = { kind: 'error', reason: 'run_failed' }
    } finally {
      release()
      if (isLoaderUp) {
        dispatch(removeLoader(LoadersType.WORKFLOW_STEP))
      }
    }

    if (verdict.kind === 'stopped' || verdict.kind === 'sent') {
      return verdict
    }
    if (isStoppedByUnmount()) {
      return { kind: 'stopped' }
    }
    return runHandlers(args, verdict, internals)
  }

  return { runStep }
}

/** `runStep`, for a caller in code that is handed one (COM-1296). */
export type RunWorkflowStep = ReturnType<typeof useWorkflowStep>['runStep']

export default useWorkflowStep
